import type { ReactNode, CSSProperties } from "react";
import type { CmsBackground } from "@/lib/api/cms-windows";
import { backgroundToStyle } from "./bgStyle";
import styles from "./CDefault.module.css";

/**
 * CDefault — domyślny kontener dla katalogów (`c_*`).
 *
 * **Rola:** opakowanie wizualne treści katalogu — `<article>` z białym tłem,
 * ramką, paddingiem i `<h1>` page title z `imp_cms_catalog.name`. Reuse'owalny
 * przez dispatch lambda w [variants.tsx](./variants.tsx) — każdy variant
 * katalogu decyduje czy wybiera `CDefault` (h1+card), [CEmpty](./CEmpty.tsx)
 * (passthrough), czy inny wrapper.
 *
 * **Konwencja nazewnicza kontenerów (po cutover 2026-05-27):**
 *   - `C{Variant}.tsx` — kontener katalogów (`CDefault`, `CEmpty`, ...)
 *   - `W{Variant}.tsx` — kontener okien (`WDefault`, `WEmpty`, ...)
 *
 * **4 opcje konfiguracji** (props):
 *   - **nagłówek** (`title`) — `imp_cms_catalog.name` renderowany jako `<h1>`
 *     wewnątrz `<header>` z border-bottom (separator nad treścią).
 *   - **podtytuł** (`subtitle`) — opcjonalny `imp_cms_catalog.name_subtitle`
 *     renderowany jako `<p>` pod `<h1>` ALE NAD separatorem `<header>`. Gdy
 *     wypełniony, kreska oddzielająca przesuwa się pod podtytuł; gdy puste
 *     (`""`/`null`/`undefined`) - element pomijany w DOM, separator zostaje
 *     bezpośrednio pod h1.
 *   - **treść** (`children`) — content variant'a katalogu (CDescriptivePage,
 *     CContactForm, ...) renderowany w `<div class="body">`
 *   - **tło** (`background`) — `CmsBackground` aplikowany jako inline style
 *     na `<article>` (override token `--color-bg-card`)
 *
 * **Komponent zawartości NIE wie że jest w `CDefault`** — variant lambda w
 * `variants.tsx` wprost wrappuje:
 *   `c_xxx: ({ content, title, subtitle, background }) => (
 *      <CDefault title={title} subtitle={subtitle} background={background}>
 *        <CXxx content={content as CXxxContent} />
 *      </CDefault>
 *   )`
 */
export function CDefault({ title, subtitle, background, children }: CDefaultProps) {
  const bgStyle: CSSProperties | undefined = (() => {
    const s = backgroundToStyle(background);
    return Object.keys(s).length > 0 ? s : undefined;
  })();

  return (
    <article className={styles.root} style={bgStyle}>
      {(title || subtitle) && (
        <header className={styles.header}>
          {title && (
            <h1
              className={styles.title}
              dangerouslySetInnerHTML={{ __html: title }}
            />
          )}
          {subtitle && (
            <p
              className={styles.subtitle}
              dangerouslySetInnerHTML={{ __html: subtitle }}
            />
          )}
        </header>
      )}
      <div className={styles.body}>{children}</div>
    </article>
  );
}

export type CDefaultProps = {
  /** Nagłówek strony — `imp_cms_catalog.name` lub `name_header`. Może zawierać
   *  inline HTML (admin-controlled, trusted). Brak -> bez `<h1>`. */
  title?: string;
  /** Podtytuł renderowany pod `<h1>` (`imp_cms_catalog.name_subtitle`). Może
   *  zawierać inline HTML (admin-controlled, trusted). Pusty/null/undefined ->
   *  bez elementu w DOM. */
  subtitle?: string | null;
  /** Tło z `imp_cms_catalog.config[background]` przez `m_site_backgrounds`. */
  background?: CmsBackground | null;
  children: ReactNode;
};
